import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, FileText, Calendar, DollarSign, AlertCircle, Link2, RefreshCw, Layers, CheckCircle2, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

const CARDS = [
  { key: 'todays_orders',      label: "Today's Orders",     icon: ShoppingCart, bg: 'from-blue-600 to-blue-700',   ring: 'ring-blue-400/30',   tab: 'sales-orders' },
  { key: 'pending_quotes',     label: 'Pending Quotes',     icon: FileText,     bg: 'from-amber-500 to-amber-600', ring: 'ring-amber-400/30',  tab: 'quotes' },
  { key: 'new_consultations',  label: 'New Consultations',  icon: Calendar,     bg: 'from-violet-600 to-violet-700', ring: 'ring-violet-400/30', tab: 'consultations' },
  { key: 'new_quote_requests', label: 'Quote Requests',     icon: FileText,     bg: 'from-emerald-600 to-emerald-700', ring: 'ring-emerald-400/30', tab: 'quote-requests' },
  { key: 'overdue_invoices',   label: 'Overdue Invoices',   icon: AlertCircle,  bg: 'from-red-500 to-red-600',     ring: 'ring-red-400/30',    tab: 'invoices' },
  { key: 'todays_shared_designs', label: 'Designs Shared',  icon: Link2,        bg: 'from-cyan-600 to-cyan-700',   ring: 'ring-cyan-400/30',   tab: 'shared-design-analytics' },
];

export const AdminQuickStats = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [syncHealth, setSyncHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = async () => {
    try {
      const [statsRes, healthRes] = await Promise.all([
        axios.get(`${API}/api/admin/quick-stats`, { headers }),
        axios.get(`${API}/api/admin/sync-health`, { headers }).catch(() => null),
      ]);
      setStats(statsRes.data);
      if (healthRes) setSyncHealth(healthRes.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); const id = setInterval(fetchStats, 120000); return () => clearInterval(id); }, []);

  if (loading || !stats) return null;

  return (
    <div className="mb-6" data-testid="admin-quick-stats">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider">Today at a Glance</h3>
          <span className="text-[10px] text-neutral-400">{new Date(stats.as_of).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <button onClick={() => { setLoading(true); fetchStats(); }} className="text-neutral-400 hover:text-neutral-600 transition-colors" data-testid="quick-stats-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARDS.map(({ key, label, icon: Icon, bg, ring, tab }) => {
          const value = stats[key] ?? 0;
          const isAlert = key === 'overdue_invoices' && value > 0;
          return (
            <button
              key={key}
              onClick={() => onNavigate?.(tab)}
              className={`relative rounded-xl bg-gradient-to-br ${bg} p-3.5 text-white overflow-hidden group text-left cursor-pointer transition-all hover:scale-[1.03] hover:shadow-lg active:scale-[0.98] ${isAlert ? 'animate-pulse' : ''}`}
              data-testid={`quick-stat-${key}`}
            >
              <div className={`absolute -top-3 -right-3 w-14 h-14 rounded-full ring-4 ${ring} opacity-20 group-hover:opacity-30 transition-opacity`} />
              <Icon className="w-4 h-4 opacity-80 mb-1.5" />
              <div className="text-2xl font-bold leading-none">{value}</div>
              <div className="text-[11px] mt-1 opacity-80 leading-tight">{label}</div>
            </button>
          );
        })}
      </div>

      {/* Revenue + Outstanding row */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <button
          onClick={() => onNavigate?.('payments')}
          className="rounded-xl border border-neutral-200 bg-white px-4 py-3 flex items-center justify-between cursor-pointer transition-all hover:border-emerald-300 hover:shadow-md active:scale-[0.98] text-left"
          data-testid="quick-stat-revenue"
        >
          <div>
            <div className="text-xs text-neutral-500 font-medium">Today's Revenue</div>
            <div className="text-xl font-bold text-emerald-700">{money(stats.todays_revenue)}</div>
          </div>
          <DollarSign className="w-8 h-8 text-emerald-200" />
        </button>
        <button
          onClick={() => onNavigate?.('invoices')}
          className="rounded-xl border border-neutral-200 bg-white px-4 py-3 flex items-center justify-between cursor-pointer transition-all hover:border-amber-300 hover:shadow-md active:scale-[0.98] text-left"
          data-testid="quick-stat-outstanding"
        >
          <div>
            <div className="text-xs text-neutral-500 font-medium">Outstanding</div>
            <div className="text-xl font-bold text-amber-700">{money(stats.outstanding_amount)}</div>
          </div>
          <AlertCircle className="w-8 h-8 text-amber-200" />
        </button>
      </div>

      {/* Sync Health Badge */}
      {syncHealth && (
        <button
          onClick={() => onNavigate?.('catalog-sync')}
          className="mt-3 w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 flex items-center gap-3 cursor-pointer transition-all hover:border-emerald-300 hover:shadow-md active:scale-[0.98] text-left"
          data-testid="sync-health-badge"
        >
          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
            syncHealth.status === 'green' ? 'bg-emerald-500' :
            syncHealth.status === 'amber' ? 'bg-amber-500 animate-pulse' :
            syncHealth.status === 'red' ? 'bg-red-500 animate-pulse' : 'bg-neutral-300'
          }`} />
          <Layers className="w-5 h-5 text-neutral-400" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-neutral-500 font-medium">Fabric Catalog Sync</div>
            <div className="text-sm font-semibold text-neutral-800">
              {syncHealth.total_textures} textures — {syncHealth.catalogs_count} catalogs
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            {syncHealth.last_sync_at ? (
              <div className="flex items-center gap-1 text-xs text-neutral-400">
                {syncHealth.status === 'green' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Clock className="w-3.5 h-3.5" />}
                {new Date(syncHealth.last_sync_at).toLocaleDateString()}
              </div>
            ) : (
              <span className="text-xs text-neutral-400">Never synced</span>
            )}
            <div className="text-[10px] text-neutral-400">
              {syncHealth.auto_sync_count} auto-sync
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default AdminQuickStats;
